import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Ban, DollarSign, FileText, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatCATDate } from '../../utils/timezone';
import { getClaimCooldownInfo } from './utils';
import { printFamilyClaimHistory } from './exportUtils';

const CLAIMS_PER_PAGE = 5;

export default function FamilyView({
  familyInfo, familyMembers, familyClaimHistory,
  role, isSuperAdmin, workflowEnabled, preauthRequired = false, hospitalCurrency, formatCurrency,
  toLocalBalance, toLocalAmount,
  patientBypassStates, onToggleBypass, onCheckIn, onCreateClaimForMember, onViewClaimDetails,
}) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [familyClaimHistory]);
  // Re-evaluate cooldowns every minute so the block lifts exactly at the last minute
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const totalClaimsAmount = familyClaimHistory.reduce((total, claim) => total + claim.header.total_claim_amount, 0);
  const totalPages = Math.ceil(familyClaimHistory.length / CLAIMS_PER_PAGE);
  const paginatedClaims = familyClaimHistory.slice((page - 1) * CLAIMS_PER_PAGE, page * CLAIMS_PER_PAGE);

  return (
    <div className="mt-6 space-y-4">
      {familyInfo.is_suspended && (
        <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
          <div className="flex items-start gap-3">
            <Ban className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-red-800">Family Account Suspended</h4>
              {familyInfo.suspension_reason && (
                <p className="text-sm text-red-900 mt-1" data-testid="family-suspension-reason"><span className="font-semibold">Reason:</span> {familyInfo.suspension_reason}</p>
              )}
              <p className="text-sm text-red-700 mt-1">
                {familyInfo.suspension_reason ? 'Please contact System Administrator or Acacia Insurance office.' : (familyInfo.suspended_message || "This family account is suspended. Please contact System Administrator or Acacia Insurance office.")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Family Balance Card */}
      <div className={`p-6 bg-gradient-to-br rounded-xl border ${
        familyInfo.is_suspended
          ? 'from-gray-100 to-white border-gray-300'
          : familyInfo.remaining_balance <= 0
            ? 'from-red-50 to-white border-red-300'
            : familyInfo.remaining_balance < 100
              ? 'from-yellow-50 to-white border-yellow-300'
              : 'from-green-50 to-white border-green-200'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
              Family: {familyInfo.family_id}
              {familyInfo.is_suspended && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">SUSPENDED</span>
              )}
            </h3>
            <p className="text-sm text-gray-600">Principal: {familyInfo.principle_member_name}</p>
          </div>
          <div className={`bg-gradient-to-br px-8 py-6 rounded-2xl shadow-lg text-center ${
            familyInfo.is_suspended
              ? 'from-gray-400 to-gray-500 text-white'
              : familyInfo.remaining_balance <= 0
                ? 'from-red-500 to-red-600 text-white'
                : familyInfo.remaining_balance < 100
                  ? 'from-yellow-500 to-yellow-600 text-white'
                  : 'from-green-500 to-green-600 text-white'
          }`}>
            <DollarSign className="w-8 h-8 mx-auto mb-2" />
            <div className="text-sm font-medium opacity-90">Family Balance</div>
            <div className="text-3xl font-bold">{formatCurrency(toLocalBalance(familyInfo.remaining_balance))}</div>
            {!isSuperAdmin && hospitalCurrency.code !== 'USD' && (
              <div className="text-xs opacity-75 mt-1">
                (≈ ${familyInfo.remaining_balance.toFixed(2)} USD)
              </div>
            )}
          </div>
        </div>
        {!familyInfo.is_suspended && familyInfo.remaining_balance <= 0 && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg flex items-start gap-2">
            <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">⚠️ Insufficient Balance</p>
              <p className="text-xs text-red-700 mt-1">This family has no remaining balance. Claims cannot be submitted until balance is recharged.</p>
            </div>
          </div>
        )}
        {!familyInfo.is_suspended && familyInfo.remaining_balance > 0 && familyInfo.remaining_balance < 100 && (
          <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg flex items-start gap-2">
            <Ban className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-800">⚠️ Low Balance Warning</p>
              <p className="text-xs text-yellow-700 mt-1">This family has a low balance (less than ${100}). Please advise them to recharge soon.</p>
            </div>
          </div>
        )}
      </div>

      {/* Family Members Table */}
      <div className="bg-white rounded-xl border border-blue-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
          <h3 className="font-bold text-blue-900">Family Members ({familyMembers.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Serial Number</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Name</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">DOB</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Sex</th>
                <th className="text-left p-3 text-sm font-semibold text-gray-700">Relationship</th>
                {(role === 'Finance' || role === 'Admin' || role === 'Reception' || isSuperAdmin) && (
                  <th className="text-center p-3 text-sm font-semibold text-gray-700">Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {familyMembers.map((member) => {
                const cooldownInfo = getClaimCooldownInfo(member.last_claim_date, now);
                const memberSuspended = member.is_suspended || member.status === 'Suspended';
                return (
                  <tr key={member.serial_number} className={`border-b border-gray-100 hover:bg-gray-50 ${memberSuspended ? 'bg-red-50' : ''}`}>
                    <td className="p-3 text-sm font-medium text-gray-800">{member.serial_number}</td>
                    <td className="p-3 text-sm text-gray-800">
                      {member.first_name} {member.middle_name} {member.last_name}
                      {memberSuspended && (
                        <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">SUSPENDED</span>
                      )}
                      {memberSuspended && member.suspension_reason && !familyInfo.is_suspended && (
                        <div className="text-xs text-red-700 mt-0.5" data-testid={`member-suspension-reason-${member.serial_number}`}><span className="font-semibold">Reason:</span> {member.suspension_reason}</div>
                      )}
                    </td>
                    <td className="p-3 text-sm text-gray-600">{member.dob}</td>
                    <td className="p-3 text-sm text-gray-600">{member.sex}</td>
                    <td className="p-3 text-sm text-gray-600">{member.relationship === 'Principle' ? 'Principal' : member.relationship}</td>
                    {(role === 'Finance' || role === 'Admin' || role === 'Reception' || isSuperAdmin) && (
                      <td className="p-3 text-center">
                        {memberSuspended ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                              Account Suspended
                            </span>
                            <span className="text-xs text-red-600">Contact Admin</span>
                          </div>
                        ) : (() => {
                          const bypassEnabled = patientBypassStates[member.serial_number] || false;
                          // The 14-day treatment interval does not apply at pre-authorization hospitals (a stamped Acacia code is required instead)
                          const canCreate = cooldownInfo.canCreateClaim || bypassEnabled || (workflowEnabled && preauthRequired);

                          if (canCreate) {
                            return (
                              <div className="flex flex-col items-center gap-2">
                                {!isSuperAdmin && workflowEnabled && (
                                  <Button
                                    onClick={() => onCheckIn(member)}
                                    size="sm"
                                    className="bg-teal-600 hover:bg-teal-700"
                                    data-testid={`checkin-${member.serial_number}`}
                                  >
                                    Check In
                                  </Button>
                                )}
                                {(isSuperAdmin || !workflowEnabled) && (
                                  <Button
                                    onClick={() => onCreateClaimForMember(member)}
                                    size="sm"
                                    variant="outline"
                                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                    data-testid={`create-claim-${member.serial_number}`}
                                  >
                                    Create Claim
                                  </Button>
                                )}
                                {!cooldownInfo.canCreateClaim && bypassEnabled && isSuperAdmin && (
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => onToggleBypass(member.serial_number)}
                                      className="relative inline-flex h-5 w-9 items-center rounded-full bg-green-500 transition-colors"
                                      data-testid={`bypass-toggle-${member.serial_number}`}
                                      title="Bypass ON - Will auto-disable after treatment"
                                    >
                                      <span className="inline-block h-3 w-3 transform rounded-full bg-white translate-x-5" />
                                    </button>
                                    <span className="text-xs text-green-600 font-medium" title="One-time override - resets after claim">Bypass ON</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return (
                            <div className="flex flex-col items-center gap-2">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${cooldownInfo.endsWithin24h ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-orange-100 text-orange-800'}`} data-testid={`cooldown-badge-${member.serial_number}`}>
                                {cooldownInfo.endsWithin24h ? cooldownInfo.nextTreatmentTime : cooldownInfo.nextTreatmentDate}
                              </span>
                              <span className="text-xs text-gray-500" data-testid={`cooldown-remaining-${member.serial_number}`}>Next Treatment • {cooldownInfo.remainingLabel}</span>
                              {isSuperAdmin && (
                                <div className="flex items-center gap-2 mt-1">
                                  <button
                                    onClick={() => onToggleBypass(member.serial_number)}
                                    className="relative inline-flex h-5 w-9 items-center rounded-full bg-gray-300 transition-colors hover:bg-gray-400"
                                    data-testid={`bypass-toggle-${member.serial_number}`}
                                    title="Enable one-time bypass to allow treatment"
                                  >
                                    <span className="inline-block h-3 w-3 transform rounded-full bg-white translate-x-1" />
                                  </button>
                                  <span className="text-xs text-gray-500 font-medium" title="Click to enable one-time bypass">Bypass OFF</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Family Claim History */}
      {familyClaimHistory.length > 0 && (
        <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-6 py-4 border-b border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="font-bold text-purple-900 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Previous Claims History ({familyClaimHistory.length} Total)
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => printFamilyClaimHistory(familyInfo, familyClaimHistory)}
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Print
                </Button>
              </div>
              <div className="text-right">
                <p className="text-sm text-purple-700 font-medium">Total Amount of All Claims</p>
                <p className="text-2xl font-bold text-purple-900">
                  {formatCurrency(toLocalAmount(totalClaimsAmount))}
                </p>
                {!isSuperAdmin && hospitalCurrency.code !== 'USD' && (
                  <p className="text-xs text-purple-600">
                    (≈ ${totalClaimsAmount.toFixed(2)} USD)
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Claim ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Hospital</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClaims.map((claim) => (
                  <tr
                    key={claim.header.claim_id}
                    className="border-b border-gray-100 hover:bg-purple-50 cursor-pointer transition-colors"
                    onClick={() => onViewClaimDetails(claim)}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{claim.header.claim_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{claim.header.patient_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700" data-testid="family-claim-hospital">{claim.header.hospital_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCATDate(claim.header.timestamp)}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 text-right font-medium">
                      {formatCurrency(toLocalAmount(claim.header.total_claim_amount))}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewClaimDetails(claim);
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-purple-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((page - 1) * CLAIMS_PER_PAGE) + 1} to {Math.min(page * CLAIMS_PER_PAGE, familyClaimHistory.length)} of {familyClaimHistory.length} claims
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <span className="text-sm text-gray-700 px-3">
                  Page {page} of {totalPages}
                </span>
                <Button
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
